
import { collection, addDoc, serverTimestamp, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';

export interface AppLogError {
  id?: string;
  timestamp: any;
  userId: string | null;
  userEmail: string | null;
  errorMessage: string;
  stack?: string;
  operationType?: string;
  path?: string;
  context?: any;
  severity: 'low' | 'medium' | 'high' | 'critical';
  deviceInfo: {
    userAgent: string;
    platform: string;
    screenSize: string;
  };
}

/**
 * Sanitizes an arbitrary context object breaking circular references and functions.
 */
const safeSanitizeContext = (obj: any, maxDepth = 4, seen = new WeakSet()): any => {
  if (obj === null || typeof obj !== 'object') {
    if (typeof obj === 'function') return '[Function]';
    return obj;
  }
  if (maxDepth <= 0 || seen.has(obj)) {
    return '[Complex/Circular Object]';
  }
  seen.add(obj);

  if (obj instanceof Error) {
    return {
      name: obj.name,
      message: obj.message,
      stack: obj.stack
    };
  }

  if (Array.isArray(obj)) {
    return obj.slice(0, 20).map(item => safeSanitizeContext(item, maxDepth - 1, seen));
  }

  const result: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    try {
      const val = obj[key];
      if (val !== undefined && typeof val !== 'function') {
        result[key] = safeSanitizeContext(val, maxDepth - 1, seen);
      }
    } catch {
      result[key] = '[Unreadable Property]';
    }
  }
  return result;
};

export const logAppError = async (
  message: string, 
  severity: AppLogError['severity'] = 'medium', 
  context: any = {}
) => {
  try {
    const sanitizedContext = safeSanitizeContext(context);
    const errorLog: Partial<AppLogError> = {
      timestamp: serverTimestamp(),
      userId: auth.currentUser?.uid || 'anonymous',
      userEmail: auth.currentUser?.email || 'anonymous',
      errorMessage: message,
      stack: new Error().stack,
      severity,
      context: sanitizedContext,
      deviceInfo: {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
        screenSize: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'unknown'
      }
    };

    console.warn('[AppLogger]', message, sanitizedContext);
    
    if (db && auth.currentUser) {
      await addDoc(collection(db, 'app_logs'), errorLog);
    }
  } catch (err) {
    console.warn('Failed to log error to Firebase safely:', err);
  }
};

// Hook-like function to subscribe to logs (for the Analysis tab)
export const subscribeToLogs = (callback: (logs: AppLogError[]) => void) => {
  if (!db) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, 'app_logs'), 
    orderBy('timestamp', 'desc'), 
    limit(100)
  );

  return onSnapshot(q, (snapshot) => {
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as AppLogError[];
    callback(logs);
  }, (err) => {
    console.warn('Notice fetching logs (likely permissions):', err?.message || err);
    callback([]);
  });
};
